import { useState, useEffect, useCallback } from 'react';
// api
import { accountApiService } from 'src/api/account-api';
// types
import { HTTaiKhoan, OptionModel } from 'src/types/api-response';

// ----------------------------------------------------------------------

export function useAccount() {
  const [accounts, setAccounts] = useState<HTTaiKhoan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusOptions, setStatusOptions] = useState<OptionModel[]>([]);
  const [roleOptions, setRoleOptions] = useState<OptionModel[]>([]);

  // 1. Lấy danh sách tài khoản
  const fetchAccounts = useCallback(async (params?: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountApiService.listTaiKhoan(params);

      if (response.messCode === 1 && response.items) {
        setAccounts(response.items);
      } else {
        setError(response.message || 'Không thể tải danh sách tài khoản');
      }
    } catch (err: any) {
      console.error('Error fetching accounts:', err);
      setError(err.message || 'Không thể tải danh sách tài khoản');
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchOptions = useCallback(async () => {
    try {
      // Chạy song song 2 API để tối ưu tốc độ
      const [roleRes, statusRes] = await Promise.all([
        accountApiService.optionVaiTro(),
        accountApiService.optionTrangThaiTaiKhoan(),
      ]);

      // Xử lý Option Vai Trò
      if (roleRes.messCode === 1 && roleRes.items) {
        setRoleOptions(roleRes.items);
      }

      // Xử lý Option Trạng Thái
      if (statusRes.messCode === 1 && statusRes.items) {
        setStatusOptions(statusRes.items);
      }
    } catch (err: any) {
      console.error('Error fetching options:', err);
      // Không block luồng chính nếu lỗi options, chỉ log error
    }
  }, []);
  // 2. Lấy chi tiết tài khoản theo ID
  const getAccountById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountApiService.getTaiKhoanById(id);
      if (response.messCode === 1 && response.oneItem) {
        return response.oneItem;
      }
      throw new Error(response.message || 'Không thể lấy thông tin tài khoản');
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 3. Cập nhật tài khoản (Admin)
  const updateAccount = useCallback(
    async (id: string, data: Partial<HTTaiKhoan>) => {
      setLoading(true);
      setError(null);
      try {
        const response = await accountApiService.updateTaiKhoanById(id, data);
        if (response.messCode === 1) {
          await fetchAccounts(); // Làm mới danh sách
          return response.oneItem;
        }
        throw new Error(response.message || 'Cập nhật tài khoản thất bại');
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchAccounts]
  );

  // 4. Đổi mật khẩu tài khoản (Admin)
  const updatePassword = useCallback(async (id: string, newPass: string) => {
    setLoading(true);
    try {
      const response = await accountApiService.updatePasswordById(id, newPass);
      if (response.messCode !== 1) {
        throw new Error(response.message || 'Đổi mật khẩu thất bại');
      }
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 5. Lấy thông tin cá nhân (Profile)
  const getMyProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await accountApiService.getMyTaiKhoan();
      if (response.messCode === 1) return response.oneItem;
      throw new Error(response.message || 'Không thể tải tải thông tin');
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

 // 6. Cập nhật thông tin cá nhân
  const updateMyProfile = useCallback(async (data: Partial<HTTaiKhoan>) => {
    setLoading(true);
    try {
      const response = await accountApiService.updateMyTaiKhoan(data);
      if (response.messCode !== 1) {
        throw new Error(response.message || 'Cập nhật thông tin thất bại');
      }
      return response.oneItem;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // 7. Tự đổi mật khẩu
  const changeMyPassword = useCallback(async (data : any) => {
    setLoading(true);
    try {
      // API yêu cầu truyền Partial<HTTaiKhoan> chứa matKhau
      const prarms = {
        matKhau: data.oldPassword,
        matKhauMoi: data.newPassword
      }
      const response = await accountApiService.updateMyPassword(prarms);
      if (response.messCode !== 1) {
        throw new Error(response.message || 'Đổi mật khẩu thất bại');
      }
      return response;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Tự động load danh sách khi mount
  useEffect(() => {
    fetchAccounts();
    fetchOptions();
  }, [fetchAccounts,fetchOptions]);

  return {
    accounts,
    loading,
    error,
    fetchAccounts,
    statusOptions,
    roleOptions,
    fetchOptions,
    getAccountById, 
    updateAccount,
    updatePassword,
    getMyProfile,
    updateMyProfile,
    changeMyPassword,
    refetch: fetchAccounts,
  };
}
